export interface GreenKeyConfig {
  thresholdOpaque: number
  thresholdTransparent: number
}

export interface KeyedCanvasTarget {
  src: string
  loop: boolean
  onEnded?: () => void
}

export interface KeyedCanvasPanel {
  video: HTMLVideoElement
  canvas: HTMLCanvasElement
}

interface KeyedCanvasCrossfaderOptions {
  width: number
  height: number
  fadeMs: number
  key: GreenKeyConfig
  onLoadError?: (fatal: boolean, src: string) => void
  /** True only after a keyed frame is actually composited and visible. */
  onVisibleFrameChange?: (visible: boolean) => void
  /**
   * Read-only projection port for spatial hosts. The canvas remains owned by
   * this renderer; consumers may upload its current pixels but must not draw
   * into it. Emitted only after a decoded frame was keyed, and reset on stop.
   */
  onFrameCanvasChanged?: (canvas: HTMLCanvasElement | null) => void
  scheduleFrame?: (callback: FrameRequestCallback) => number
  cancelFrame?: (handle: number) => void
  scheduleTimeout?: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>
  cancelTimeout?: (handle: ReturnType<typeof setTimeout>) => void
}

const defaultScheduleFrame = (callback: FrameRequestCallback): number => {
  if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(callback)
  return setTimeout(() => callback(performance.now()), 0) as unknown as number
}

const defaultCancelFrame = (handle: number): void => {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle)
  else clearTimeout(handle)
}

/**
 * Applies the measured FULLBODY green key in place.
 *
 * Background distance is G-max(R,B). Pixels below t0 remain opaque, pixels
 * above t1 become transparent, and the interval is feathered. Despill caps G
 * to max(R,B) wherever green dominates so keyed edges do not glow.
 */
export function keyGreenPixels(pixels: Uint8ClampedArray, config: GreenKeyConfig): void {
  const range = Math.max(1, config.thresholdTransparent - config.thresholdOpaque)
  for (let index = 0; index < pixels.length; index += 4) {
    const red = pixels[index]
    const green = pixels[index + 1]
    const blue = pixels[index + 2]
    const maxRedBlue = Math.max(red, blue)
    const metric = green - maxRedBlue

    let alpha = 255
    if (metric >= config.thresholdTransparent) alpha = 0
    else if (metric > config.thresholdOpaque) {
      alpha = Math.round(255 * (1 - (metric - config.thresholdOpaque) / range))
    }
    pixels[index + 3] = alpha

    if (alpha > 0 && metric > 0 && green > maxRedBlue) pixels[index + 1] = maxRedBlue
  }
}

/** Draws and keys one decoded source frame. Returns false until one exists. */
export function drawKeyedFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  key: GreenKeyConfig,
): boolean {
  if (video.readyState < 2 || video.videoWidth === 0) return false
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return false
  if (canvas.width !== width) canvas.width = width
  if (canvas.height !== height) canvas.height = height
  context.drawImage(video, 0, 0, width, height)
  const image = context.getImageData(0, 0, width, height)
  keyGreenPixels(image.data, key)
  context.putImageData(image, 0, 0)
  return true
}

/**
 * Double-buffered keyed renderer. Videos decode behind transparent canvases;
 * the outgoing keyed canvas remains visible until the incoming canvas has
 * painted a real frame. Loading errors never blank the active panel.
 */
export class GreenScreenCanvasCrossfader {
  private readonly panels: readonly [KeyedCanvasPanel, KeyedCanvasPanel]
  private readonly options: KeyedCanvasCrossfaderOptions
  private readonly scheduleFrame: (callback: FrameRequestCallback) => number
  private readonly cancelFrame: (handle: number) => void
  private readonly scheduleTimeout: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>
  private readonly cancelTimeout: (handle: ReturnType<typeof setTimeout>) => void
  private readonly useVideoFrameCallbacks: boolean
  private readonly renderFrames: [number | null, number | null] = [null, null]
  private readonly renderFrameKinds: [('video' | 'animation') | null, ('video' | 'animation') | null] = [null, null]
  private readonly renderGenerations: [number, number] = [0, 0]

  private activeIndex: 0 | 1 = 0
  private activeSrc: string | null = null
  private targetSrc: string | null = null
  private generation = 0
  private pendingCleanup: (() => void) | null = null
  private endedCleanup: (() => void) | null = null
  private pauseTimer: ReturnType<typeof setTimeout> | null = null

  constructor(
    panels: readonly [KeyedCanvasPanel, KeyedCanvasPanel],
    options: KeyedCanvasCrossfaderOptions,
  ) {
    this.panels = panels
    this.options = options
    this.scheduleFrame = options.scheduleFrame ?? defaultScheduleFrame
    this.cancelFrame = options.cancelFrame ?? defaultCancelFrame
    this.useVideoFrameCallbacks = options.scheduleFrame == null
    this.scheduleTimeout = options.scheduleTimeout ?? ((callback, delayMs) => setTimeout(callback, delayMs))
    this.cancelTimeout = options.cancelTimeout ?? ((handle) => clearTimeout(handle))

    for (const { video, canvas } of panels) {
      video.crossOrigin = 'anonymous'
      video.muted = true
      video.playsInline = true
      video.preload = 'auto'
      canvas.width = options.width
      canvas.height = options.height
      canvas.style.opacity = '0'
      canvas.style.transition = 'none'
      canvas.style.zIndex = '1'
    }
  }

  setTarget(target: KeyedCanvasTarget): void {
    if (this.targetSrc === target.src) {
      if (this.activeSrc === target.src) void this.panels[this.activeIndex].video.play().catch(() => undefined)
      return
    }

    this.generation += 1
    const generation = this.generation
    this.clearPending()
    this.clearEnded()
    this.clearPauseTimer()

    const hasActive = this.activeSrc != null
    const incomingIndex: 0 | 1 = hasActive ? (this.activeIndex === 0 ? 1 : 0) : this.activeIndex
    const incoming = this.panels[incomingIndex]
    const outgoing = hasActive ? this.panels[this.activeIndex] : null
    this.stopRendering(incomingIndex)
    this.targetSrc = target.src

    incoming.video.pause()
    incoming.video.loop = target.loop
    incoming.canvas.style.transition = 'none'
    incoming.canvas.style.opacity = '0'
    incoming.canvas.style.zIndex = '2'
    if (outgoing) {
      outgoing.canvas.style.transition = 'none'
      outgoing.canvas.style.opacity = '1'
      outgoing.canvas.style.zIndex = '1'
    }

    let committed = false
    let paintOne: number | null = null
    let paintTwo: number | null = null
    let retryOnCanPlay: (() => void) | null = null

    const cleanupPendingListeners = () => {
      incoming.video.removeEventListener('playing', onPlayable)
      incoming.video.removeEventListener('timeupdate', onPlayable)
      incoming.video.removeEventListener('error', onError)
      if (retryOnCanPlay) incoming.video.removeEventListener('canplay', retryOnCanPlay)
      retryOnCanPlay = null
      if (paintOne != null) this.cancelFrame(paintOne)
      if (paintTwo != null) this.cancelFrame(paintTwo)
      paintOne = null
      paintTwo = null
    }

    const commit = () => {
      if (committed || generation !== this.generation) return
      committed = true
      cleanupPendingListeners()
      incoming.canvas.style.transition = `opacity ${this.options.fadeMs}ms linear`
      incoming.canvas.style.opacity = '1'
      if (outgoing) {
        outgoing.canvas.style.transition = `opacity ${this.options.fadeMs}ms linear`
        outgoing.canvas.style.opacity = '0'
      }
      this.activeIndex = incomingIndex
      this.activeSrc = target.src
      this.targetSrc = target.src
      this.pendingCleanup = null
      this.options.onVisibleFrameChange?.(true)
      this.options.onFrameCanvasChanged?.(incoming.canvas)

      if (target.onEnded) {
        const onEnded = () => target.onEnded?.()
        incoming.video.addEventListener('ended', onEnded, { once: true })
        this.endedCleanup = () => incoming.video.removeEventListener('ended', onEnded)
      }
      if (outgoing) {
        const outgoingIndex: 0 | 1 = incomingIndex === 0 ? 1 : 0
        this.pauseTimer = this.scheduleTimeout(() => {
          if (generation === this.generation) {
            outgoing.video.pause()
            this.stopRendering(outgoingIndex)
          }
          this.pauseTimer = null
        }, this.options.fadeMs + 40)
      }
    }

    const onFirstKeyedFrame = () => {
      if (committed || paintOne != null || generation !== this.generation) return
      // Keep the old panel for two paints after the first successfully keyed
      // frame, so compositing catches up before opacity changes.
      paintOne = this.scheduleFrame(() => {
        paintOne = null
        paintTwo = this.scheduleFrame(() => {
          paintTwo = null
          commit()
        })
      })
    }

    const onPlayable = () => {
      if (generation !== this.generation) return
      this.startRendering(incomingIndex, onFirstKeyedFrame, onError)
    }

    const onError = () => {
      if (generation !== this.generation || committed) return
      cleanupPendingListeners()
      this.stopRendering(incomingIndex)
      incoming.video.pause()
      incoming.canvas.style.opacity = '0'
      this.targetSrc = this.activeSrc
      this.pendingCleanup = null
      if (!hasActive) this.options.onVisibleFrameChange?.(false)
      this.options.onLoadError?.(!hasActive, target.src)
    }

    incoming.video.addEventListener('playing', onPlayable)
    incoming.video.addEventListener('timeupdate', onPlayable)
    incoming.video.addEventListener('error', onError, { once: true })
    this.pendingCleanup = cleanupPendingListeners
    incoming.video.src = target.src
    incoming.video.load()
    void incoming.video.play().catch(() => {
      if (generation !== this.generation || committed) return
      retryOnCanPlay = () => {
        retryOnCanPlay = null
        if (generation === this.generation) void incoming.video.play().catch(() => undefined)
      }
      incoming.video.addEventListener('canplay', retryOnCanPlay, { once: true })
    })
  }

  stop(): void {
    this.generation += 1
    this.clearPending()
    this.clearEnded()
    this.clearPauseTimer()
    this.activeSrc = null
    this.targetSrc = null
    for (const index of [0, 1] as const) {
      this.stopRendering(index)
      this.panels[index].video.pause()
      this.panels[index].canvas.style.transition = 'none'
      this.panels[index].canvas.style.opacity = '0'
    }
    this.options.onVisibleFrameChange?.(false)
    this.options.onFrameCanvasChanged?.(null)
  }

  destroy(): void {
    this.stop()
    for (const { video } of this.panels) {
      video.removeAttribute('src')
      video.load()
    }
  }

  private startRendering(
    index: 0 | 1,
    onFirstFrame: () => void,
    onRenderError: () => void,
  ): void {
    if (this.renderFrames[index] != null) return
    const panel = this.panels[index]
    const renderGeneration = ++this.renderGenerations[index]
    let painted = false
    let lastMediaTime = -1
    const draw = () => {
      if (renderGeneration !== this.renderGenerations[index]) {
        this.renderFrames[index] = null
        this.renderFrameKinds[index] = null
        return
      }
      try {
        // requestAnimationFrame is only the scheduler. Pixel work runs once
        // per decoded media frame, not once per 60/120 Hz display refresh.
        if (panel.video.currentTime !== lastMediaTime) {
          if (drawKeyedFrame(
            panel.video,
            panel.canvas,
            this.options.width,
            this.options.height,
            this.options.key,
          )) {
            lastMediaTime = panel.video.currentTime
            if (this.activeSrc != null && this.activeIndex === index) {
              this.options.onFrameCanvasChanged?.(panel.canvas)
            }
            if (!painted) {
              painted = true
              onFirstFrame()
            }
          }
        }
      } catch (error) {
        console.warn('[ArcturianFullbody] Green-key frame failed:', error)
        this.renderFrames[index] = null
        this.renderFrameKinds[index] = null
        onRenderError()
        return
      }
      this.renderFrames[index] = this.scheduleRenderFrame(index, draw)
    }
    this.renderFrames[index] = this.scheduleRenderFrame(index, draw)
  }

  private stopRendering(index: 0 | 1): void {
    this.renderGenerations[index] += 1
    const frame = this.renderFrames[index]
    const kind = this.renderFrameKinds[index]
    const videoFrameApi = this.panels[index].video as unknown as {
      cancelVideoFrameCallback?: (handle: number) => void
    }
    if (frame != null && kind === 'video' && videoFrameApi.cancelVideoFrameCallback) {
      videoFrameApi.cancelVideoFrameCallback(frame)
    } else if (frame != null) {
      this.cancelFrame(frame)
    }
    this.renderFrames[index] = null
    this.renderFrameKinds[index] = null
  }

  /**
   * Video-frame callbacks keep the keying loop attached to decoded media,
   * rather than to the window animation clock that an immersive XR session
   * may throttle. Older browsers retain the requestAnimationFrame fallback.
   */
  private scheduleRenderFrame(index: 0 | 1, callback: FrameRequestCallback): number {
    const video = this.panels[index].video
    const videoFrameApi = video as unknown as {
      requestVideoFrameCallback?: (callback: (now: DOMHighResTimeStamp) => void) => number
    }
    if (this.useVideoFrameCallbacks && videoFrameApi.requestVideoFrameCallback) {
      this.renderFrameKinds[index] = 'video'
      return videoFrameApi.requestVideoFrameCallback((now) => callback(now))
    }
    this.renderFrameKinds[index] = 'animation'
    return this.scheduleFrame(callback)
  }

  private clearPending(): void {
    this.pendingCleanup?.()
    this.pendingCleanup = null
  }

  private clearEnded(): void {
    this.endedCleanup?.()
    this.endedCleanup = null
  }

  private clearPauseTimer(): void {
    if (this.pauseTimer == null) return
    this.cancelTimeout(this.pauseTimer)
    this.pauseTimer = null
  }
}
